/**
 * byo-v4-added.js — the screen after Add To Cart (P6).
 *
 * Ported from `byo-stages-v3.jsx` `AddedScreen`. DESIGN-SPEC.md §7.
 *
 * The heading rises at the seal cue and the buttons arrive at a flat 1.0s, so
 * the copy lands with the sticker rather than waiting out the whole scene.
 * Tapping the scene skips it; tapping the buttons does not.
 *
 * "Go To Cart" is a plain navigation to the cart page (`config.routes.cart`). That is deliberate and is the
 * one safe way to show the cart: the theme refreshes its drawer by handling its
 * own `shapes:cart:*` events and re-dispatching `input` on every quantity
 * field, which submits quantity changes and deleted a bundle in testing
 * (GIFTSHIP-CONTRACT.md). A location change also bypasses Giftship's cart-link
 * interception, so it does not pop Giftship's drawer.
 *
 * ES2020 module. View layer: assets/byo-v4-vendor.js (Preact + htm).
 */

import { html, useState, useEffect } from './byo-v4-vendor.js';
const { Btn } = (window.BYOV4 = window.BYOV4 || {});
const { TissueScene, tissueTiming, pulseHeaderBag } = (window.BYOV4 = window.BYOV4 || {});
const { unitsOf } = (window.BYOV4 = window.BYOV4 || {});
export function AddedScreen({ b, config, quick, boxesInCart, onBuildAnother }) {
  const t = tissueTiming(quick);
  const units = unitsOf(b);

  const [sealed, setSealed] = useState(false);
  const [showBtns, setShowBtns] = useState(false);

  useEffect(() => {
    const a = setTimeout(() => setSealed(true), t.sealAt * 1000);
    /* The design's flat 1.0s, not a function of the scene length. */
    const c = setTimeout(() => setShowBtns(true), 1000);
    return () => {
      clearTimeout(a);
      clearTimeout(c);
    };
  }, []);

  const recipient = b.box.recipient;
  /* Counts as words (D70). */
  const W = (n) => (window.BYO && window.BYO.Words ? window.BYO.Words(n) : String(n));
  const w = (n) => (window.BYO && window.BYO.words ? window.BYO.words(n) : String(n));
  const summary = [
    `${W(b.itemCount)} item${b.itemCount === 1 ? '' : 's'} · ${b.money(b.total)}`,
    boxesInCart > 1 ? `${w(boxesInCart)} boxes in the cart` : null,
    recipient ? `for ${recipient}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return html`
    <div class="byo-v4-added byo-v4-fill__inner">
      <div class="byo-v4-added__col">
        <${TissueScene}
          units=${units}
          quick=${quick}
          onDone=${() => {
            /* The bag pulses as the parcel lands, not when the request came
               back — DESIGN-SPEC.md §3.4. */
            pulseHeaderBag();
            setSealed(true);
            setShowBtns(true);
          }}
          onSkip=${() => {
            setSealed(true);
            setShowBtns(true);
          }}
        />

        ${sealed &&
        html`<h2 class="byo-v4-added__h">Wrapt and in your cart</h2>
          <p class="byo-v4-added__sum">${summary}</p>`}

        ${showBtns &&
        html`<div class="byo-v4-added__btns">
            <${Btn} size="md" onClick=${() => {
              window.location.href = (config.routes && config.routes.cart) || '/cart';
            }}>Go To Cart<//>
            <${Btn} variant="outline" size="md" onClick=${onBuildAnother}>Build Another Box<//>
          </div>
          <p class="byo-v4-added__foot">
            ${(config.copy && config.copy.multishipHint) ||
            'Sending to multiple people. Build a box each, then set the addresses in the cart.'}
          </p>`}
      </div>
    </div>
  `;
}

/* Published for the siblings that used to `import` this file. See the
   section's loader: relative imports drop the ?v= and are served from a
   year-long CDN cache, so a sibling could be a build behind. */
Object.assign((window.BYOV4 = window.BYOV4 || {}), { AddedScreen });
